/**
 * Operational window — ``/settings/misc``.
 *
 * Build-up start + take-down end date pickers backing the singleton
 * misc_settings row. GET on mount, PATCH on save. Either / both dates
 * may be null (window not configured).
 *
 * The festival itself is not represented here — it is a tagged exclusion
 * row (``source='festival'``) managed on the Estimation exclusions page.
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  InputNumber,
  Space,
  Typography,
  notification,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";

import { ApiError, api } from "../../api/client";

const { Title, Paragraph } = Typography;

interface MiscFormValues {
  buildup_start_date: Dayjs | null;
  takedown_end_date: Dayjs | null;
}

export const MiscSettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // Volunteer-compensation per-day rate (cents). (add-volunteer-compensation)
  const [rateCents, setRateCents] = useState<number>(100);
  const [rateSaving, setRateSaving] = useState(false);
  // We don't have a row-level updated_at exposed on the operational-window
  // endpoint (it returns just the two fields), so If-Match is best-effort
  // skipped — concurrent edits on this singleton are extremely rare.
  const [form] = Form.useForm<MiscFormValues>();

  const saveRate = async () => {
    setRateSaving(true);
    try {
      const r = await api.patchVolunteerRate(rateCents);
      setRateCents(r.volunteer_comp_cents_per_day);
      notification.success({ message: "Volunteer rate saved", duration: 1.5 });
    } catch (err) {
      notification.error({
        message: "Save failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setRateSaving(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [row, rate] = await Promise.all([
        api.getOperationalWindow(),
        api.getVolunteerRate(),
      ]);
      form.setFieldsValue({
        buildup_start_date: row.buildup_start_date
          ? dayjs(row.buildup_start_date)
          : null,
        takedown_end_date: row.takedown_end_date
          ? dayjs(row.takedown_end_date)
          : null,
      });
      setRateCents(rate.volunteer_comp_cents_per_day);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    let values: MiscFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const { buildup_start_date, takedown_end_date } = values;
    if (
      buildup_start_date &&
      takedown_end_date &&
      takedown_end_date.isBefore(buildup_start_date)
    ) {
      setError("Take-down end date must be on or after build-up start date.");
      return;
    }
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      await api.patchOperationalWindow({
        buildup_start_date: buildup_start_date
          ? buildup_start_date.format("YYYY-MM-DD")
          : null,
        takedown_end_date: takedown_end_date
          ? takedown_end_date.format("YYYY-MM-DD")
          : null,
      });
      setInfo("Saved.");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card loading={loading}>
      <Title level={2} style={{ marginBottom: 8 }}>
        Operational window
      </Title>
      <Paragraph type="secondary">
        Build-up start anchors reports and forecasts to the start of the
        season (default {`"from"`} for expense filters, base for the
        estimator basis window). Take-down end is the absolute cap for
        forecast horizons and for users without an explicit departure date.
        The festival itself is tagged as a separate exclusion row under
        Estimation exclusions; these two dates describe the broader
        operational window.
      </Paragraph>
      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {info && (
        <Alert
          type="success"
          message={info}
          closable
          onClose={() => setInfo(null)}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form<MiscFormValues> form={form} layout="vertical">
        <Form.Item label="Build-up start date" name="buildup_start_date">
          <DatePicker allowClear format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label="Take-down end date" name="takedown_end_date">
          <DatePicker allowClear format="DD.MM.YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Space>
          <Button type="primary" loading={saving} onClick={() => void onSave()}>
            Save
          </Button>
          <Button onClick={() => void refresh()}>Reset</Button>
        </Space>
      </Form>
      </Card>

      <Card loading={loading}>
        <Title level={2} style={{ marginBottom: 8 }}>
          Volunteer compensation
        </Title>
        <Paragraph type="secondary">
          The amount credited per volunteer day at checkout (kiosk + operator
          settlement). Stored in cents — 100&nbsp;ct = €1.00.
        </Paragraph>
        <Space align="center">
          <span>Per-day value</span>
          <InputNumber
            min={0}
            max={1000000}
            value={rateCents}
            onChange={(v) => setRateCents(typeof v === "number" ? v : 0)}
            addonAfter="ct"
            style={{ width: 160 }}
          />
          <Typography.Text type="secondary">
            = {(rateCents / 100).toFixed(2)} € / day
          </Typography.Text>
          <Button
            type="primary"
            loading={rateSaving}
            onClick={() => void saveRate()}
          >
            Save
          </Button>
        </Space>
      </Card>
    </Space>
  );
};

export default MiscSettingsPage;
